import { z } from 'zod';

import { supportedTokens } from '@/config';
import { reportFallback } from '@/lib/report-fallback';

/**
 * A token's own ERC-20 `symbol()` beside its registry code (api v1.160.0).
 *
 * `symbol` is the ROWA asset registry's code for an asset. It is an
 * identifier (a backtest names its assets by it), and on mainnet a few codes
 * are not tickers: `MORPHO_STEAK_USDC` is the token `steakUSDC`. Every
 * response that names a token by its code now also serves `tokenSymbol`,
 * read on chain, and null when it is unread, failed, or not a plausible
 * ticker. Screens print `tokenSymbol ?? symbol`, through `displaySymbol`
 * and nothing else; nothing derives a symbol from anything else.
 *
 * One exception, and it lives here too: a token the app configures keeps
 * the name the app gives it. Sepolia's tUSDC answers `symbol()` with
 * "USDC", and the deposit form calls it tUSDC; one token has one name.
 */

/** The configured tokens' addresses, lowercase, on every chain. */
const CONFIGURED_TOKENS = new Set(
  Object.values(supportedTokens).flatMap((network) =>
    network.tokens.map((token) => token.tokenAddress.toLowerCase())
  )
);

/** Whether the app configures this token (by address; any chain). */
export function isConfiguredToken(address: string): boolean {
  return CONFIGURED_TOKENS.has(address.toLowerCase());
}

/** The backend's own rule for a plausible ticker: 1 to 32 of A–Z a–z 0–9 . _ + / - */
const TOKEN_SYMBOL = /^[A-Za-z0-9._+/-]{1,32}$/;

/** Whether a value is a ticker a screen may print. */
export function isTokenSymbol(value: unknown): value is string {
  return typeof value === 'string' && TOKEN_SYMBOL.test(value);
}

/**
 * The boundary read of a served `tokenSymbol`. Absent (an older api) and
 * null (unread) are silent; anything else reads as null and is reported
 * once under `contract`, by the field's name only, never its value.
 */
export function tokenSymbolField(feature: string, contract: string) {
  return z
    .string()
    .regex(TOKEN_SYMBOL)
    .nullish()
    .catch(reportFallback(feature, contract, null));
}

/**
 * The one way a token is named on screen: its own symbol when it was read,
 * else the registry symbol (or whatever the caller named it by before),
 * kept as it is, null included. Given the token's `address`, a token the
 * app configures keeps `symbol` whatever its own symbol says.
 */
export function displaySymbol<Fallback extends string | null | undefined>(
  tokenSymbol: string | null | undefined,
  symbol: Fallback,
  address?: string | null
): string | Fallback {
  if (address && isConfiguredToken(address)) return symbol;
  return isTokenSymbol(tokenSymbol) ? tokenSymbol : symbol;
}
